import React, { useMemo } from "react";
import { every } from "lodash";
import DoughnutChart from "@charty/doughnut";
import { getMetricLabel } from "@linkfluence/i18n";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import { DatavizDoughnutChart } from "@radarly/models/dataviz";
import { FullFacet } from "@radarly/models/facets";
import { SearchFragmentTerm, getTermFromAggs } from "@radarly/models/search";
import { useRangeFromFragment } from "../../data";
import Empty from "../../empty";
import Tooltip from "../../tooltip/metric";
import { getSeries } from "../data";

export interface SharedWidgetTermDoughnutChartProps {
  loading: boolean;
  data: SearchFragmentTerm["data"];
  fragment: SearchFragmentTerm;
  dataviz: DatavizDoughnutChart;
  facet: FullFacet;
  disableAnimation?: boolean;
  className?: string;
  onClickLegend?: (uid: string | number) => void;
  onClickPie?: (uid: string | number) => void;
  isFullscreen?: boolean;
}

export default function SharedWidgetTermDoughnutChart({
  fragment,
  facet,
  onClickPie,
  className,
  onClickLegend,
  data,
  loading,
  disableAnimation,
  isFullscreen,
}: SharedWidgetTermDoughnutChartProps) {
  const locale = useCurrentUserLocale();
  const range = useRangeFromFragment(fragment, loading);
  const field = getTermFromAggs(fragment.request.aggs);
  const metric = fragment.request.aggs[field].metrics[0];
  const dataset = useMemo(() => {
    return getSeries(data, facet, metric, locale);
  }, [data, metric, facet, locale]);
  const emptyData = useMemo(() => every(dataset, { count: 0 }), [dataset]);
  return (
    <Empty empty={emptyData}>
      <DoughnutChart
        locale={locale}
        series={dataset}
        className={className}
        orderBy="numeric"
        onClickPie={onClickPie}
        onClickLegend={onClickLegend}
        centerLabel={getMetricLabel(locale, metric)}
        disableAnimation={disableAnimation}
        tooltip={(serie) => (
          <Tooltip
            range={range}
            count={serie.count}
            color={serie.color}
            label={serie.label}
            metric={metric}
            locale={locale}
          />
        )}
        isFullscreen={isFullscreen}
      />
    </Empty>
  );
}
